import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { Loader2, Archive, Search } from "lucide-react";

const YEARS = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i);
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function OldPickupRequests() {
  const [tab, setTab] = useState("completed");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchOldPickupRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { archive_reason: tab, limit: 100 };
      if (year) params.year = year;
      if (year && month) params.month = month;
      if (search) params.search = search;
      const res = await api.get("/v2/old-pickup-requests", { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load old pickup requests");
    } finally {
      setLoading(false);
    }
  }, [tab, year, month, search]);

  useEffect(() => { fetchOldPickupRequests(); }, [fetchOldPickupRequests]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase flex items-center gap-2">
            <Archive className="w-6 h-6" /> Old Pickup Requests
          </h1>
          <p className="text-slate-500 text-sm">
            {total} archived pickup requests · admin-only view · nothing here is ever deleted
          </p>
        </div>

        <div className="flex gap-2 border-b border-slate-200">
          <button
            onClick={() => setTab("completed")}
            data-testid="old-pur-tab-completed"
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "completed" ? "border-brand-500 text-brand-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            Completed
          </button>
          <button
            onClick={() => setTab("cancelled")}
            data-testid="old-pur-tab-cancelled"
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "cancelled" ? "border-brand-500 text-brand-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            Cancelled
          </button>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search pickup ID or Delhivery PUR number..."
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500"
            />
          </div>
          <select value={year} onChange={(e) => { setYear(e.target.value); setMonth(""); }}
            className="border border-slate-200 rounded-sm text-sm px-3 py-2">
            <option value="">All Years</option>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {year && (
            <select value={month} onChange={(e) => setMonth(e.target.value)}
              className="border border-slate-200 rounded-sm text-sm px-3 py-2">
              <option value="">All Months</option>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          )}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <Archive className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No archived {tab} pickup requests found for this filter.</p>
            </div>
          ) : (
            <div className="space-y-3 p-3">
              {items.map((r) => (
                <div key={r.id || r.pickup_id} data-testid="old-pur-row" className="p-4 bg-white border border-slate-200 rounded-xl">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-[220px]">
                      <div className="font-heading font-bold text-brand-600 text-sm">
                        {r.pickup_id}
                      </div>
                      {r.delhivery_pur_number && (
                        <div className="text-xs text-slate-400 font-normal mt-0.5">
                          Delhivery: {r.delhivery_pur_number}
                        </div>
                      )}
                      <div className="text-xs text-slate-500 mt-1">
                        {r.network_type} · {r.pickup_location_id}
                      </div>
                    </div>
                    <div className="flex gap-6">
                      <div>
                        <div className="text-slate-400 uppercase text-[10px] font-bold mb-0.5">Shipments</div>
                        <div className="font-semibold text-slate-700 text-sm">{(r.shipment_ids || []).length}</div>
                      </div>
                      <div>
                        <div className="text-slate-400 uppercase text-[10px] font-bold mb-0.5">Picked</div>
                        <div className="font-semibold text-slate-700 text-sm">{r.picked_awb_count || 0}</div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 min-w-[110px] items-end">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                        r.status === "COMPLETED" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                      }`}>
                        {r.status}
                      </span>
                      <div className="text-xs text-slate-400 whitespace-nowrap">
                        {tab === "completed"
                          ? (r.picked_at ? new Date(r.picked_at).toLocaleDateString("en-IN") : "—")
                          : (r.cancelled_at ? new Date(r.cancelled_at).toLocaleDateString("en-IN")
                             : (r.auto_cancelled_at ? new Date(r.auto_cancelled_at).toLocaleDateString("en-IN") : "—"))}
                      </div>
                      {tab === "cancelled" && (r.cancelled_reason || r.auto_cancelled_reason) && (
                        <div className="text-[10px] text-slate-400 text-right max-w-[150px]" title={r.cancelled_reason || r.auto_cancelled_reason}>
                          {r.cancelled_reason || r.auto_cancelled_reason}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
